import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { PM_SHELL_TOP_BAR_HOST, type PmShellTopBarHost } from './pm-shell-top-bar-host';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { toast } from '../../../shared/components/pm-toast/pm-toast';
import { PmTooltipComponent } from '../../../shared/components/pm-tooltip/pm-tooltip.component';
import { PmUserContextService } from '../../../core/services/pm-user-context.service';
import { PmFirmBrandingService } from '../../../core/services/pm-firm-branding.service';
import { PmGlobalSearchApiService } from '../../../core/services/api/pm-global-search-api.service';
import { PmAnnouncementsApiService } from '../../../core/services/api/pm-announcements-api.service';
import { PmTooltipDirective } from '../../../shared/components/pm-tooltip/pm-tooltip.directive';

/** Workspace top bar: logo and firm branding, the main nav links (folding into a hamburger when they do not fit), global search box, Quick Add, app launcher, document hub, tickets, support, the notifications dropdown and the profile button. */
@Component({
  selector: 'pm-shell-top-bar',
  standalone: true,
  imports: [PmTooltipComponent, PmTooltipDirective],
  templateUrl: './pm-shell-top-bar.component.html',
  styleUrl: './pm-shell-top-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PmShellTopBarComponent {
  /** Read by the workspace: checkTopNavFit() measures the bar, and the search popup is placed under the search box. */
  @ViewChild('headerSearchWrapper') headerSearchWrapper?: ElementRef<HTMLElement>;
  @ViewChild('topNavbar') topNavbarEl?: ElementRef<HTMLElement>;
  @ViewChild('topNavLeft') topNavLeftEl?: ElementRef<HTMLElement>;
  @ViewChild('topNavRight') topNavRightEl?: ElementRef<HTMLElement>;
  @ViewChild('topNavMenu') topNavMenuEl?: ElementRef<HTMLElement>;

  private readonly router = inject(Router);

  /** Public for template bindings (`practiceContextReady`, `practiceContextLoading`, …). */
  readonly userContext = inject(PmUserContextService);

  private readonly pmAnnouncementsApi = inject(PmAnnouncementsApiService);


  /** What this section still reads from the workspace (see pm-shell-top-bar-host.ts). */
  private readonly workspace = inject(PM_SHELL_TOP_BAR_HOST);

  get activeDropdown() {
    return this.workspace.activeDropdown;
  }

  get activeItem() {
    return this.workspace.activeItem;
  }

  get announcements() {
    return this.workspace.announcements;
  }

  checkTopNavFit(...args: Parameters<PmShellTopBarHost['checkTopNavFit']>): ReturnType<PmShellTopBarHost['checkTopNavFit']> {
    return this.workspace.checkTopNavFit(...args);
  }

  get docHubStore() {
    return this.workspace.docHubStore;
  }

  get documentHubPage() {
    return this.workspace.documentHubPage;
  }

  effectiveActingUserId(...args: Parameters<PmShellTopBarHost['effectiveActingUserId']>): ReturnType<PmShellTopBarHost['effectiveActingUserId']> {
    return this.workspace.effectiveActingUserId(...args);
  }

  effectivePracticeParentUserId(...args: Parameters<PmShellTopBarHost['effectivePracticeParentUserId']>): ReturnType<PmShellTopBarHost['effectivePracticeParentUserId']> {
    return this.workspace.effectivePracticeParentUserId(...args);
  }

  ensureWorkspaceProfileFromTeam(...args: Parameters<PmShellTopBarHost['ensureWorkspaceProfileFromTeam']>): ReturnType<PmShellTopBarHost['ensureWorkspaceProfileFromTeam']> {
    return this.workspace.ensureWorkspaceProfileFromTeam(...args);
  }

  getCustomLabel(...args: Parameters<PmShellTopBarHost['getCustomLabel']>): ReturnType<PmShellTopBarHost['getCustomLabel']> {
    return this.workspace.getCustomLabel(...args);
  }

  get headerAnnouncementsLoading() {
    return this.workspace.headerAnnouncementsLoading;
  }

  get headerAnnouncementsPreview() {
    return this.workspace.headerAnnouncementsPreview;
  }

  get headerAnnouncementsUnreadCount() {
    return this.workspace.headerAnnouncementsUnreadCount;
  }

  get headerGlobalSearchActiveIndex() {
    return this.workspace.headerGlobalSearchActiveIndex;
  }

  get headerGlobalSearchDebounceTimer() {
    return this.workspace.headerGlobalSearchDebounceTimer;
  }
  set headerGlobalSearchDebounceTimer(value: PmShellTopBarHost['headerGlobalSearchDebounceTimer']) {
    this.workspace.headerGlobalSearchDebounceTimer = value;
  }

  get headerGlobalSearchDebouncing() {
    return this.workspace.headerGlobalSearchDebouncing;
  }

  get headerGlobalSearchError() {
    return this.workspace.headerGlobalSearchError;
  }

  get headerGlobalSearchFlatResults() {
    return this.workspace.headerGlobalSearchFlatResults;
  }

  get headerGlobalSearchHasSearched() {
    return this.workspace.headerGlobalSearchHasSearched;
  }

  get headerGlobalSearchLoading() {
    return this.workspace.headerGlobalSearchLoading;
  }

  get headerGlobalSearchQuery() {
    return this.workspace.headerGlobalSearchQuery;
  }

  get headerGlobalSearchRequestSub() {
    return this.workspace.headerGlobalSearchRequestSub;
  }
  set headerGlobalSearchRequestSub(value: PmShellTopBarHost['headerGlobalSearchRequestSub']) {
    this.workspace.headerGlobalSearchRequestSub = value;
  }

  get headerGlobalSearchResults() {
    return this.workspace.headerGlobalSearchResults;
  }

  get isAppLauncherOpen() {
    return this.workspace.isAppLauncherOpen;
  }

  get isMobileNavOpen() {
    return this.workspace.isMobileNavOpen;
  }

  get isNotificationOpen() {
    return this.workspace.isNotificationOpen;
  }

  get isProfileMenuOpen() {
    return this.workspace.isProfileMenuOpen;
  }

  get isQuickAddOpen() {
    return this.workspace.isQuickAddOpen;
  }

  get isSearchExpanded() {
    return this.workspace.isSearchExpanded;
  }

  onHeaderGlobalSearchResultClick(...args: Parameters<PmShellTopBarHost['onHeaderGlobalSearchResultClick']>): ReturnType<PmShellTopBarHost['onHeaderGlobalSearchResultClick']> {
    return this.workspace.onHeaderGlobalSearchResultClick(...args);
  }

  get profile() {
    return this.workspace.profile;
  }

  refreshHeaderAnnouncementsData(...args: Parameters<PmShellTopBarHost['refreshHeaderAnnouncementsData']>): ReturnType<PmShellTopBarHost['refreshHeaderAnnouncementsData']> {
    return this.workspace.refreshHeaderAnnouncementsData(...args);
  }

  stopProp(...args: Parameters<PmShellTopBarHost['stopProp']>): ReturnType<PmShellTopBarHost['stopProp']> {
    return this.workspace.stopProp(...args);
  }

  stopPropAllowSpaceInFields(...args: Parameters<PmShellTopBarHost['stopPropAllowSpaceInFields']>): ReturnType<PmShellTopBarHost['stopPropAllowSpaceInFields']> {
    return this.workspace.stopPropAllowSpaceInFields(...args);
  }

  toggleQuickAdd(...args: Parameters<PmShellTopBarHost['toggleQuickAdd']>): ReturnType<PmShellTopBarHost['toggleQuickAdd']> {
    return this.workspace.toggleQuickAdd(...args);
  }

  get topNavCollapsed() {
    return this.workspace.topNavCollapsed;
  }

  updateHeaderGlobalSearchModalPlacement(...args: Parameters<PmShellTopBarHost['updateHeaderGlobalSearchModalPlacement']>): ReturnType<PmShellTopBarHost['updateHeaderGlobalSearchModalPlacement']> {
    return this.workspace.updateHeaderGlobalSearchModalPlacement(...args);
  }

  get visibleNavItems() {
    return this.workspace.visibleNavItems;
  }

  private readonly pmGlobalSearchApi = inject(PmGlobalSearchApiService);

  /** Public for template bindings (`logoUrl`) — swaps the sidebar brand mark when a firm logo is uploaded. */
  readonly firmBranding = inject(PmFirmBrandingService);

  goToTicketsHub(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    void this.router.navigateByUrl('/support-tickets');
  }

  activeNotificationTab = signal('Notifications');

  headerAnnouncementsMarkingAllRead = signal(false);

  readonly headerNotificationBadgeCount = computed(() => this.headerAnnouncementsUnreadCount());

  viewAllFromNotificationsDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isNotificationOpen.set(false);
    const tab = this.activeNotificationTab();
    if (tab === 'Announcements') {
      void this.router.navigateByUrl('/announcements');
    } else {
      void this.router.navigateByUrl('/notifications');
    }
  }

  toggleNotifications(event?: Event) {
    event?.stopPropagation?.();
    const opening = !this.isNotificationOpen();
    this.isNotificationOpen.set(opening);
    this.activeDropdown.set(null); // Close other dropdowns
    this.isQuickAddOpen.set(false);
    if (opening) {
      this.refreshHeaderAnnouncementsData(true);
    }
  }

  /** Hamburger toggle for the collapsed top-nav panel. */
  toggleMobileNav(event?: Event): void {
    event?.stopPropagation?.();
    this.isMobileNavOpen.update((v) => !v);
    if (this.isMobileNavOpen()) {
      this.isNotificationOpen.set(false);
      this.isQuickAddOpen.set(false);
    }
  }

  markHeaderAnnouncementsAllRead(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const parentUserId = this.userContext.firmParentUserIdForApi();
    const actingUserId = this.userContext.actingUserMasterIdForApi();
    if (parentUserId <= 0 || actingUserId <= 0) {
      toast.error('Practice context is not ready.');
      return;
    }

    this.headerAnnouncementsMarkingAllRead.set(true);
    this.pmAnnouncementsApi
      .markAllRead(parentUserId, actingUserId)
      .subscribe({
        next: () => {
          this.headerAnnouncementsMarkingAllRead.set(false);
          toast.success('All announcements marked as read.');
          this.refreshHeaderAnnouncementsData(true);
        },
        error: () => {
          this.headerAnnouncementsMarkingAllRead.set(false);
          toast.error('Could not mark announcements as read.');
        },
      });
  }

  toggleAppLauncher(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.isAppLauncherOpen.update((v) => !v);
    if (this.isAppLauncherOpen()) {
      this.isQuickAddOpen.set(false);
      this.isNotificationOpen.set(false);
      this.isProfileMenuOpen.set(false);
      this.activeDropdown.set(null);
    }
  }

  toggleProfileMenu(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.isProfileMenuOpen.update((v) => !v);
    if (this.isProfileMenuOpen()) {
      this.isQuickAddOpen.set(false);
      this.isNotificationOpen.set(false);
      this.isAppLauncherOpen.set(false);
      this.activeDropdown.set(null);
      void this.ensureWorkspaceProfileFromTeam();
    }
  }

  setNotificationTab(tab: string, event: Event) {
    event.stopPropagation();
    this.activeNotificationTab.set(tab);
  }

  toggleSearch() {
    this.isSearchExpanded.update(val => !val);
    if (this.isSearchExpanded()) {
      // The immediate measurement runs before .search-wrapper's own 0.3s width transition has
      // moved at all, so it captures the still-collapsed ~27px box rather than the expanded one —
      // harmless as long as a later keystroke re-measures once things have settled (see
      // onHeaderGlobalSearchInput), but this follow-up measurement, timed to land just after the
      // transition finishes, means the placement is already correct even before the user types
      // anything (e.g. while the results-count placeholder or a very fast first keystroke is still
      // mid-transition).
      queueMicrotask(() => this.updateHeaderGlobalSearchModalPlacement());
      setTimeout(() => {
        if (this.isSearchExpanded()) {
          this.updateHeaderGlobalSearchModalPlacement();
        }
      }, 320);
    }
    if (!this.isSearchExpanded()) {
      this.headerGlobalSearchLoading.set(false);
      this.headerGlobalSearchDebouncing.set(false);
      this.headerGlobalSearchHasSearched.set(false);
      this.headerGlobalSearchError.set(null);
      this.headerGlobalSearchResults.set([]);
      this.headerGlobalSearchRequestSub?.unsubscribe();
      if (this.headerGlobalSearchDebounceTimer) {
        clearTimeout(this.headerGlobalSearchDebounceTimer);
        this.headerGlobalSearchDebounceTimer = null;
      }
    }
  }

  onHeaderGlobalSearchInput(value: string): void {
    const query = value ?? '';
    this.headerGlobalSearchQuery.set(query);
    this.updateHeaderGlobalSearchModalPlacement();
    this.headerGlobalSearchError.set(null);
    this.headerGlobalSearchHasSearched.set(false);

    if (this.headerGlobalSearchDebounceTimer) {
      clearTimeout(this.headerGlobalSearchDebounceTimer);
      this.headerGlobalSearchDebounceTimer = null;
    }

    const trimmed = query.trim();
    if (trimmed.length < 3) {
      this.headerGlobalSearchLoading.set(false);
      this.headerGlobalSearchDebouncing.set(false);
      this.headerGlobalSearchResults.set([]);
      this.headerGlobalSearchActiveIndex.set(0);
      this.headerGlobalSearchRequestSub?.unsubscribe();
      return;
    }

    this.headerGlobalSearchDebouncing.set(true);
    this.headerGlobalSearchDebounceTimer = setTimeout(() => {
      this.runHeaderGlobalSearch(trimmed);
    }, 400);
  }

  private runHeaderGlobalSearch(query: string): void {
    this.headerGlobalSearchDebouncing.set(false);
    const parentUserId = this.effectivePracticeParentUserId();
    const actingUserId = this.effectiveActingUserId();
    if (parentUserId <= 0 || actingUserId <= 0) {
      this.headerGlobalSearchLoading.set(false);
      this.headerGlobalSearchHasSearched.set(true);
      this.headerGlobalSearchResults.set([]);
      return;
    }

    this.headerGlobalSearchRequestSub?.unsubscribe();
    this.headerGlobalSearchLoading.set(true);
    this.headerGlobalSearchRequestSub = this.pmGlobalSearchApi
      .search({
        parentUserId,
        actingUserId,
        searchName: query,
        take: 50
      })
      .pipe(finalize(() => this.headerGlobalSearchLoading.set(false)))
      .subscribe({
        next: (res) => {
          this.headerGlobalSearchHasSearched.set(true);
          this.headerGlobalSearchError.set(null);
          this.headerGlobalSearchResults.set(res?.items ?? []);
          this.headerGlobalSearchActiveIndex.set(0);
        },
        error: () => {
          this.headerGlobalSearchHasSearched.set(true);
          this.headerGlobalSearchError.set('Could not load search results.');
          this.headerGlobalSearchResults.set([]);
          this.headerGlobalSearchActiveIndex.set(0);
        }
      });
  }

  onHeaderGlobalSearchKeydown(event: KeyboardEvent): void {
    const flat = this.headerGlobalSearchFlatResults();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.isSearchExpanded.set(false);
      return;
    }

    if (flat.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.min(this.headerGlobalSearchActiveIndex() + 1, flat.length - 1);
      this.headerGlobalSearchActiveIndex.set(next);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.max(this.headerGlobalSearchActiveIndex() - 1, 0);
      this.headerGlobalSearchActiveIndex.set(next);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const active = flat[this.headerGlobalSearchActiveIndex()];
      if (active) {
        this.onHeaderGlobalSearchResultClick(active);
      }
    }
  }

  setActive(label: string) {
    const item = this.visibleNavItems().find(i => i.label === label);
    if (item) {
      this.activeItem.set(label);
      this.router.navigateByUrl(item.link);
    }
    this.isMobileNavOpen.set(false);
  }

  /** Opens practice Document Hub (/document-hub). Second click on the header icon while already there closes an open folder. */
  openPracticeDocumentHubFromNavIcon(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const path = this.router.url.split('?')[0];
    if (path === '/document-hub' && this.docHubStore.documentHubOpenFolderId()) {
      this.documentHubPage?.closeDocumentHubFolder();
      return;
    }
    void this.router.navigateByUrl('/document-hub');
  }
}
